import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import {
  AccessTimeOutlined,
  CloseRounded,
  DescriptionOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { amber, grey } from "@mui/material/colors";
import BasicModal from "../../modals/BasicModal";
import FileUploadField from "../../inputs/FileUploadField";
import SelectAutoInput from "../../inputs/SelectAutoInput";
import InputWrapper from "../../inputs/shared/InputWrapper";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import { selectOptionsLoanProductType } from "../../../constants/selectOptions/selectOptionsLoanProductType";
import { selectOptionsLoanPurpose } from "../../../constants/selectOptions/selectOptionsLoanPurpose";
import { useCorrespondentOptions } from "../../../hooks/useCorrespondentOptions";
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { useModelFieldOptions } from "../../../hooks/useModelFieldOptions";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";

// Sizer Import → Create Loan.
//
// Upload a loan sizer .xlsx (DSCR / GUC / MF / RTL — product auto-detected, with
// a fallback to the standardized named-range intake) and pick the correspondent
// the loan belongs to, then create.
//
// WHY TWO CALLS INSTEAD OF ONE. The one-shot route
// (POST /underwriting/new_loan/import_from_excel) takes only `uploadFile` +
// `create_salesforce_opportunity` — there is NO field on it for a
// correspondent. It resolves one server-side from the uploading user's
// company_name, and explicitly skips internal EV staff, so every internal
// upload died on the model's hard requirement:
//   "correspondent originator is required to create a loan
//    (set originator or correspondentCompanyId)"
// (UnderwritingBaseModel.create_loan → resolve_required_correspondent).
// So we parse and create as two steps instead:
//   1. POST /underwriting/new_loan/populate_loan_form → the populated but
//      UNSAVED loan object (nothing is written).
//   2. POST /underwriting/create_loan with that object + the chosen
//      correspondentCompanyId — a full loan body, which is the only create path
//      that accepts a correspondent today.
// Then we replicate what the one-shot route did after its insert: file the
// workbook as a 'Terms & Sizer' / 'Sizer' loan document, and create the
// Salesforce opportunity if asked.
//
// If the backend adds a correspondent_company_id Form param to
// import_from_excel, this collapses back to the single call.
const SizerImportModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const { populateLoanFormFromExcel, createLoan, createSalesforceOpportunity } =
    useUnderwritingHook();
  const { uploadLoanDocs } = useLoanFilesHook();
  // Correspondent objects (id → companyName) from the cached /correspondents/list.
  const correspondentOptions = useCorrespondentOptions();
  // Values are matched by string equality server-side, so they come from the
  // model schema; the constants are only the not-loaded-yet fallback.
  const productTypeOptions = useModelFieldOptions(
    "loanProductType",
    selectOptionsLoanProductType,
  );
  const loanPurposeOptions = useModelFieldOptions(
    "loanPurpose",
    selectOptionsLoanPurpose,
  );

  // FileUploadField yields { id, file, name } wrappers — keep the latest one.
  const [selected, setSelected] = useState(null);
  const [correspondentId, setCorrespondentId] = useState(null);
  const [loanProductType, setLoanProductType] = useState(null);
  const [loanPurpose, setLoanPurpose] = useState(null);
  const [createSalesforce, setCreateSalesforce] = useState(false);
  // Local, because this flow spans several requests — the hook's shared
  // axiosPostIsLoading flips back to false between them and the modal would
  // flicker out of its busy state mid-create.
  const [busyMessage, setBusyMessage] = useState(null);

  const busy = busyMessage !== null;
  const missingCorrespondent = !correspondentId;

  const reset = () => {
    setSelected(null);
    setCorrespondentId(null);
    setLoanProductType(null);
    setLoanPurpose(null);
    setCreateSalesforce(false);
    setBusyMessage(null);
  };

  const handleClose = () => {
    if (busy) return; // don't interrupt an in-flight create
    reset();
    onClose?.(false);
  };

  const finish = (loanId) => {
    reset();
    onClose?.(false);
    if (loanId) navigate(`/loan/${loanId}`);
  };

  const handleCreate = () => {
    if (busy) return;
    // Belt-and-braces: the Create button is disabled without both of these.
    if (!selected || missingCorrespondent) return;

    const file = selected?.file instanceof File ? selected.file : selected;

    setBusyMessage("Reading the sizer…");
    populateLoanFormFromExcel({
      excel: file,
      onFailFn: () => setBusyMessage(null),
      onSuccessFn: (parsed) => {
        setBusyMessage("Creating the loan…");
        createLoan({
          // Spread the parse FIRST so an explicit pick overrides what the
          // workbook said; leaving either blank keeps the sizer's own value.
          data: {
            ...(parsed || {}),
            correspondentCompanyId: correspondentId,
            ...(loanProductType ? { loanProductType } : {}),
            ...(loanPurpose ? { loanPurpose } : {}),
          },
          onFailFn: () => setBusyMessage(null),
          onSuccessFn: async (loan) => {
            const loanId = loan?._id;
            if (!loanId) {
              finish(null);
              return;
            }

            // Everything below is best-effort: the loan already exists, so a
            // failure here must not strand the user in the modal.
            setBusyMessage("Filing the sizer on the loan…");
            await uploadLoanDocs({
              loanId,
              files: [
                {
                  id: uuidv4(),
                  file,
                  name: file.name,
                  file_display_name: file.name,
                  docGroup: "Terms & Sizer",
                  docType: "Sizer",
                },
              ],
              onSuccessMsg: null,
              onFailFn: () => {},
            });

            if (createSalesforce) {
              setBusyMessage("Creating the Salesforce opportunity…");
              await createSalesforceOpportunity({
                dealId: loanId,
                onFailFn: () => {},
              });
            }

            finish(loanId);
          },
        });
      },
    });
  };

  return (
    <BasicModal
      show={show}
      onClose={handleClose}
      maxWidth="sm"
      icon={<UploadFileOutlined className="thin" />}
      title="Create Loan from Sizer"
      description="Upload a loan sizer spreadsheet to create a new loan."
      bodySx={{ p: 2.5 }}
    >
      <Flx column gap={2}>
        {selected ? (
          <Flx
            ac
            jb
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${grey[300]}`,
              background: grey[50],
            }}
          >
            <Flx ac gap={1} sx={{ minWidth: 0 }}>
              <DescriptionOutlined sx={{ color: "primary.main" }} />
              <Txt
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selected.name}
              </Txt>
            </Flx>
            {busy ? null : (
              <IconButton size="small" onClick={() => setSelected(null)}>
                <CloseRounded sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Flx>
        ) : (
          <FileUploadField
            uploadText="Drag & drop or click to select a sizer .xlsx"
            accept=".xlsx,.xlsm,.xls"
            // Re-key on reset so the internal file list clears between opens.
            id="sizer-import-upload"
            onFilesAdded={(files) => {
              const latest = files?.[files.length - 1];
              if (latest) setSelected(latest);
            }}
          />
        )}

        {/* The one field the sizer can't supply — amounts, dates, borrowers and
            properties all come out of the workbook, and none of those are
            required by the create endpoint. The correspondent is. */}
        <InputWrapper label="Correspondent" required>
          <SelectAutoInput
            value={correspondentId}
            options={correspondentOptions}
            onChange={(v) => setCorrespondentId(v)}
            placeholder="Select a correspondent"
          />
        </InputWrapper>

        {/* Optional overrides. The sizer sets both from the workbook and the
            backend requires neither, so blank = trust the sizer; a pick wins.
            Useful when the sizer is the wrong template for the deal, or its
            purpose cell is blank. */}
        <Flx gap={2} sx={{ flexWrap: "wrap" }}>
          <InputWrapper
            label="Loan Product Type"
            helperText="Read from the sizer. Set only to override."
            sx={{ flex: 1, minWidth: 200 }}
          >
            <SelectAutoInput
              value={loanProductType}
              options={productTypeOptions}
              onChange={(v) => setLoanProductType(v)}
              placeholder="From sizer"
            />
          </InputWrapper>
          <InputWrapper
            label="Loan Purpose"
            helperText="Read from the sizer. Set only to override."
            sx={{ flex: 1, minWidth: 200 }}
          >
            <SelectAutoInput
              value={loanPurpose}
              options={loanPurposeOptions}
              onChange={(v) => setLoanPurpose(v)}
              placeholder="From sizer"
            />
          </InputWrapper>
        </Flx>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={createSalesforce}
              disabled={busy}
              onChange={(e) => setCreateSalesforce(e.target.checked)}
            />
          }
          label={
            <Txt sx={{ fontSize: 13 }}>
              Also create a matching Salesforce Opportunity
            </Txt>
          }
        />

        {busy ? (
          <Flx
            ac
            gap={1}
            sx={{
              p: 1.25,
              borderRadius: "8px",
              background: amber[50],
              border: `1px solid ${amber[200]}`,
            }}
          >
            <AccessTimeOutlined sx={{ fontSize: 18, color: amber[800] }} />
            <Txt sx={{ fontSize: 12, color: amber[900] }}>
              {busyMessage} Parsing the sizer and creating the loan can take a
              minute or so — please keep this window open until it finishes.
            </Txt>
          </Flx>
        ) : null}

        <Box sx={{ borderTop: `1px solid ${grey[200]}`, pt: 2 }}>
          <Flx ac jc="flex-end" gap={1}>
            <Button variant="text" onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!selected || missingCorrespondent || busy}
              startIcon={
                busy ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {busy ? "Creating…" : "Create Loan"}
            </Button>
          </Flx>
        </Box>
      </Flx>
    </BasicModal>
  );
};

export default SizerImportModal;
